import {useStore} from 'vuex';
import {computed} from 'vue';
import {BookingMeta, CancelBookingPayload} from '@/store/modules/booking';
import {BookingExtension, BookingFilter, BookingInterface, StoreLoaders} from '@/types';
import {debounce} from '@/utils/timing';


export enum BookingStatus {
    PENDING = 'pending',
    CONFIRMED = 'confirmed',
    CANCELLED = 'cancelled',
    COMPLETED = 'completed',
    ON_HOLD = 'on_hold',
    ARCHIVED = 'archived',
    CONCEPT = 'concept',
    RESERVED = 'reserved',
    STARTED = 'started',
    IN_PROGRESS = 'in_progress',
    STOPPED = 'stopped',
}

enum BookingSource {
    DIRECT = 'direct',
    TURO = 'turo',
    GETAROUND = 'getaround',
    // AVAIL = 'avail',
    // OTHER = 'other',
}


export interface BookingStatusChange {
    b_id: string;
    status: BookingStatus;
}

export function useBookings() {
    const store = useStore();

    const searchTopicOptions = [
        {title: 'Booking ID', value: 'b_id'},
        {title: 'Renter', value: 'renter'},
        {title: 'Listing', value: 'listing'},
    ]

    const bookings = computed<BookingInterface[]>(() => store.getters['booking/bookings']);
    const abandonedBookings = computed<BookingInterface[]>(() => store.getters['booking/abandonedBookings']);

    const booking = computed<BookingInterface>(() => store.getters['booking/booking']);
    const priorityBookings = computed<BookingInterface[]>(() => store.getters['booking/priorityBookings']);
    const bookingLoading = computed<StoreLoaders>(() => store.getters['booking/bookingLoading']);
    const bookingForm = computed(() => store.getters['booking/bookingForm']);
    const bookingNotFound = computed(() => store.getters['booking/notFound']);
    const bookingMeta = computed<BookingMeta>(() => store.getters['booking/meta']);

    const totalBookings = computed(() => store.getters['booking/totalBookings']);

    const bookingDialogs = computed(() => store.getters['booking/bookingDialogs']);

    const currentBookingStatus = computed(() => booking.value?.status ?? null);
    const currentBookingSource = computed(() => booking.value?.source ?? null);
    const currentBookingTuroTrip = computed(() => booking.value?.turo_trip ?? null);


    const paymentStatusFilterItems = computed(() => store.getters['booking/paymentStatusFilterItems']);
    const sourcesFilter = computed(() => store.getters['booking/sourcesFilter']);
    const statusFilterItems = computed(() => store.getters['booking/statusFilterItems']);
    const activityFilter = computed(() => store.getters['booking/activityFilter']);
    const startsAtFilterItems = computed(() => store.getters['booking/startsAtFilterItems']);


    const orderTableOptions = computed({
        get: () => store.getters['booking/orderTableOptions'],
        set: (value) => store.commit('booking/SET_ORDER_TABLE_OPTIONS', value),
    });

    const orderSearch = computed({
        get: () => store.getters['booking/orderSearch'],
        set: (value) => store.commit('booking/SET_ORDER_SEARCH', value),
    });


    const bookingExtensions = computed<BookingExtension[]>(() => {
        return booking.value?.extensions || [];
    });

    // Boolean computed properties
    const currentBookingIsDirect = computed(() => currentBookingSource.value === BookingSource.DIRECT);
    const currentBookingIsTuro = computed(() => currentBookingSource.value === BookingSource.TURO);


    const setBookingDialog = (subject: string, value: boolean = true) => {
        store.commit('booking/SET_BOOKING_DIALOG', {
            subject,
            value,
        });
    }

    // Actions to fetch bookings
    const fetchBookings = (options = {}) => store.dispatch('booking/fetchBookings', options);
    const fetchPriorityBookings = () => store.dispatch('booking/fetchPriorityBookings');
    const fetchSingleBooking = (bookingId: string) => store.dispatch('booking/fetchSingleBooking', bookingId);

    // Actions to modify bookings
    const cancelBooking = (payload: CancelBookingPayload) => store.dispatch('booking/cancelBooking', payload);
    const changeBookingStatus = (payload: BookingStatusChange) => store.dispatch('booking/changeBookingStatus', payload);
    const updateBooking = (payload: any) => store.dispatch('booking/updateBooking', payload);

    const bookingSourceIs = (booking: any, source: string) => {
        return String(booking?.source ?? '').toLowerCase() === source.toLowerCase();
    }

    const createBookingMeta = (payload: any) => store.dispatch('booking/createBookingMeta', payload);

    const updateBookingMeta = (booking: BookingInterface, key: string, value: any) => {
        return createBookingMeta({
            booking,
            value,
            key,
        });
    }

    const resetBookingStates = () => store.commit('booking/RESET_BOOKING_STATES');

    const extendBooking = async (payload: any) => await store.dispatch('booking/extendBooking', payload);

    // 'booking/deleteExtension'
    const deleteExtension = (payload: any) => store.dispatch('booking/deleteExtension', payload);

    const fetchCompanyBooking = (bookingId: string) => store.dispatch('booking/fetchCompanyBooking', bookingId);
    const deleteTripPhoto = (payload: any) => store.dispatch('booking/deleteTripPhoto', payload);
    const updateTripPhoto = (payload: any) => store.dispatch('booking/updateTripPhoto', payload);

    const bookingSource = (booking: BookingInterface) => {
        if (booking?.turo_trip) return 'turo';
        if (booking?.getaround_trip) return 'getaround';
        return 'fleetwire';
    };

    const fetchBookingsInit = () => {
        const filter = {} as BookingFilter;

        if (paymentStatusFilterItems.value?.length) {
            filter.payment_status = paymentStatusFilterItems.value;
        }
        if (statusFilterItems.value.length) {
            filter.status = statusFilterItems.value;
        }

        if (startsAtFilterItems.value !== null) {
            filter.starts_at_gt = startsAtFilterItems.value;
        }
        if (sourcesFilter.value.length) {
            filter.source = sourcesFilter.value;
        }

        fetchBookings({
            filter,
            ...orderTableOptions.value,
        });
    };

    const debouncedOrderSearch = debounce(() => {
        fetchBookings({
            searchTerm: orderSearch.value.search,
            searchTopic: orderSearch.value.searchTopic,
        });
    }, 500);


    return {
        bookingLoading,
        booking,
        bookings,
        priorityBookings,
        bookingNotFound,
        bookingForm,
        currentBookingStatus,
        currentBookingIsDirect,
        currentBookingIsTuro,
        currentBookingTuroTrip,
        bookingMeta,
        currentBookingSource,
        bookingDialogs,
        bookingExtensions,
        totalBookings,
        paymentStatusFilterItems,
        sourcesFilter,
        activityFilter,
        orderTableOptions,
        orderSearch,
        statusFilterItems,
        abandonedBookings,
        searchTopicOptions,
        debouncedOrderSearch,
        bookingSource,
        deleteExtension,
        setBookingDialog,
        extendBooking,
        createBookingMeta,
        updateBookingMeta,
        bookingSourceIs,
        updateBooking,
        cancelBooking,
        changeBookingStatus,
        fetchBookings,
        fetchPriorityBookings,
        fetchSingleBooking,
        fetchCompanyBooking,
        deleteTripPhoto,
        updateTripPhoto,
        resetBookingStates,
        fetchBookingsInit,
    }
}